import { AssetDTO, BucketDTO } from '../api/useAssetBucketingDetailsQuery';

interface Params {
  assetId: string;
  sourceBucketIndex: number;
  destinationBucketIndex: number;
  buckets: BucketDTO[];
  unassignedAssets: AssetDTO[];
}

export const moveAsset = ({
  assetId,
  sourceBucketIndex,
  destinationBucketIndex,
  buckets,
  unassignedAssets,
}: Params) => {
  const sourceBucket = buckets[sourceBucketIndex];
  const destinationBucket = buckets[destinationBucketIndex];

  if (
    (sourceBucketIndex !== -1 && !sourceBucket) ||
    (destinationBucketIndex !== -1 && !destinationBucket)
  ) {
    throw new Error(
      `No ${!sourceBucket ? 'source' : 'destination'} bucket with index ${
        !sourceBucket ? sourceBucketIndex : destinationBucketIndex
      } found`,
    );
  }

  const assetToMove =
    sourceBucketIndex === -1
      ? unassignedAssets.find(({ id }) => id === assetId)
      : sourceBucket.assets.find(({ id }) => id === assetId);

  if (!assetToMove) {
    throw new Error(`No asset with id ${assetId} found`);
  }

  const getNewUnassignedAssets = () => {
    if (sourceBucketIndex === -1) {
      return unassignedAssets.filter(({ id }) => id !== assetId);
    }

    if (destinationBucketIndex === -1) {
      if (assetToMove.holdings) {
        const holdingsToMove = assetToMove.holdings;
        const foundSameAccount = unassignedAssets.find(({ id }) => id === assetId);
        // There is an account with holdings already in the unassigned assets section
        // -> combine the holdings
        if (foundSameAccount) {
          return unassignedAssets.map(asset => {
            if (asset.id === foundSameAccount.id) {
              return {
                ...asset,
                holdings: [...(asset.holdings || []), ...holdingsToMove],
                value: asset.value + holdingsToMove.reduce((acc, { value }) => acc + value, 0),
                isHoldingsBucketInheritedFromAsset:
                  assetToMove.isHoldingsBucketInheritedFromAsset ||
                  asset.isHoldingsBucketInheritedFromAsset,
              };
            }

            return asset;
          });
        }
      }

      return [...unassignedAssets, assetToMove];
    }

    return unassignedAssets;
  };

  const newBuckets = buckets.map((bucket, index) => {
    if (index === sourceBucketIndex) {
      return {
        ...sourceBucket,
        assets: sourceBucket.assets.filter(({ id }) => id !== assetId),
      };
    }

    if (index === destinationBucketIndex) {
      if (assetToMove.holdings) {
        const holdingsToMove = assetToMove.holdings;
        const foundSameAccount = destinationBucket.assets.find(({ id }) => id === assetId);
        // There is an account with holdings already in the destination bucket
        // -> combine the holdings
        if (foundSameAccount) {
          return {
            ...destinationBucket,
            assets: destinationBucket.assets.map(asset => {
              if (asset.id === foundSameAccount.id) {
                return {
                  ...asset,
                  holdings: [...(asset.holdings || []), ...holdingsToMove],
                  value: asset.value + holdingsToMove.reduce((acc, { value }) => acc + value, 0),
                  isHoldingsBucketInheritedFromAsset:
                    assetToMove.isHoldingsBucketInheritedFromAsset ||
                    asset.isHoldingsBucketInheritedFromAsset,
                };
              }

              return asset;
            }),
          };
        }
      }

      return {
        ...destinationBucket,
        assets: [...destinationBucket.assets, assetToMove],
      };
    }

    return bucket;
  });

  return {
    buckets: newBuckets,
    unassignedAssets: getNewUnassignedAssets(),
  };
};
